import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calculator, TrendingUp, Landmark } from "lucide-react";
import FundingEstimator from "@/components/site/FundingEstimator";
import FactorRateConverter from "@/components/site/FactorRateConverter";
import SBAPaymentCalculator from "@/components/site/SBAPaymentCalculator";
import Seo from "@/lib/Seo";

export default function Resources() {
  return (
    <div>
      <Seo
        path="/resources"
        title="Funding Tools & Calculators — Steady Path Funding"
        description="Free interactive tools for business owners: funding estimator (10K–5M), MCA factor-to-APR translator, and an SBA loan payment calculator. Built by working brokers."
      />

      {/* Hero */}
      <section className="pt-40 pb-20 border-b border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="overline">Owner tools · Free & interactive</div>
          <h1 className="mt-8 font-serif text-6xl md:text-8xl leading-[0.9] tracking-tight max-w-5xl">
            Do the math <em className="italic text-[color:var(--gold)]">before</em> you sign.
          </h1>
          <div className="mt-10 h-px w-12 bg-[color:var(--gold)]" />

          <div className="mt-12 grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-6">
              <p className="text-[color:var(--bone)]/75 leading-relaxed text-lg">
                Three calculators we actually use with clients on real underwriting calls — figure out which products fit, translate MCA quotes into an honest APR, and forecast the true monthly payment on an SBA loan. No email required. Results are illustrative, not offers.
              </p>
            </div>
            <div className="col-span-12 md:col-span-6 md:pl-8 md:border-l border-black/10 space-y-6">
              <a
                href="#estimator"
                className="group flex items-start gap-4 text-left"
                data-testid="tool-link-estimator"
              >
                <div className="mt-1 w-10 h-10 flex items-center justify-center border border-[color:var(--gold)] text-[color:var(--gold)] group-hover:bg-[color:var(--gold)] group-hover:text-black transition-colors">
                  <TrendingUp size={16} />
                </div>
                <div>
                  <div className="font-serif text-xl group-hover:text-[color:var(--gold)] transition-colors">
                    Funding estimator
                  </div>
                  <div className="mt-1 font-mono text-[10px] tracking-[0.22em] uppercase text-[color:var(--bone)]/55">
                    $10K – $5M · Compare 7 products
                  </div>
                </div>
              </a>
              <a
                href="#factor-converter"
                className="group flex items-start gap-4 text-left"
                data-testid="tool-link-factor"
              >
                <div className="mt-1 w-10 h-10 flex items-center justify-center border border-[color:var(--gold)] text-[color:var(--gold)] group-hover:bg-[color:var(--gold)] group-hover:text-black transition-colors">
                  <Calculator size={16} />
                </div>
                <div>
                  <div className="font-serif text-xl group-hover:text-[color:var(--gold)] transition-colors">
                    MCA factor → APR translator
                  </div>
                  <div className="mt-1 font-mono text-[10px] tracking-[0.22em] uppercase text-[color:var(--bone)]/55">
                    Turn 1.35× into a real cost of capital
                  </div>
                </div>
              </a>
              <a
                href="#sba-calculator"
                className="group flex items-start gap-4 text-left"
                data-testid="tool-link-sba"
              >
                <div className="mt-1 w-10 h-10 flex items-center justify-center border border-[color:var(--gold)] text-[color:var(--gold)] group-hover:bg-[color:var(--gold)] group-hover:text-black transition-colors">
                  <Landmark size={16} />
                </div>
                <div>
                  <div className="font-serif text-xl group-hover:text-[color:var(--gold)] transition-colors">
                    SBA payment estimator
                  </div>
                  <div className="mt-1 font-mono text-[10px] tracking-[0.22em] uppercase text-[color:var(--bone)]/55">
                    Monthly payment + baseline eligibility
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Tool 1: Funding Estimator */}
      <FundingEstimator />

      {/* Tool 2: MCA Factor Rate Converter */}
      <FactorRateConverter />

      {/* Tool 3: SBA Payment Calculator */}
      <SBAPaymentCalculator />

      {/* CTA */}
      <section className="py-24 md:py-32 border-t border-black/10 bg-[color:var(--surface)]">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 text-center">
          <div className="overline">Ready when you are</div>
          <h2 className="mt-6 font-serif text-4xl md:text-6xl leading-[1] tracking-tight">
            Numbers look right? <em className="italic text-[color:var(--gold)]">Get real offers.</em>
          </h2>
          <p className="mt-6 max-w-xl mx-auto text-[color:var(--bone)]/70 leading-relaxed">
            The estimators are a starting point. Actual terms depend on your credit, revenue, and time in business. A four-minute application gets you real quotes from real lenders.
          </p>
          <Link
            to="/apply"
            data-testid="resources-cta-apply"
            className="mt-10 inline-flex items-center gap-3 bg-[color:var(--gold)] text-black px-8 py-4 font-mono text-xs tracking-[0.24em] uppercase hover:bg-[color:var(--gold-strong)] transition-colors"
          >
            Start application <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
